import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import { inputIcons } from "../../scenes/stack/environment/icons/iconExport";
import { getHomePath, navigateHome } from "../../utils/utils";
import ContainedButton from "../buttons/ContainedButton";
import OutlinedButton from "../buttons/OutlinedButton";
import {
  getTextFieldInputProps,
  StyledTextField,
  textFieldLabelProps,
} from "../forms/styles/formFieldStyles";
import { Toast } from "../Toast";

const ContactSupportModal = ({ open, onClose, errorDetails, pageUrl }) => {
  const colors = useColors();
  const { sendErrorReport } = useAPI();
  const [userMessage, setUserMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const MAX_CHARACTERS = 500;

  const handleSendReport = async () => {
    setIsSubmitting(true);
    try {
      await sendErrorReport(userMessage, errorDetails, pageUrl);
      Toast.success("Report sent! We're on it.");
      setReportSent(true);
      setUserMessage("");
    } catch (error) {
      console.error("Failed to send error report:", error);
      Toast.error("Couldn't send the report. Try again?");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setUserMessage("");
    setReportSent(false);
    onClose();
  };

  const homePath = getHomePath();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "10px",
          boxShadow: "0px 1px 4px 0px rgba(145, 158, 171, 0.24)",
        },
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={600}>
            Contact support
          </Typography>
        </DialogTitle>
        <Box
          onClick={handleClose}
          paddingRight={3}
          display="flex"
          justifyContent="center"
          sx={{ cursor: "pointer" }}
        >
          <ClearRoundedIcon
            sx={{
              width: "18px",
              height: "18px",
              color: colors.action_active,
            }}
          />
        </Box>
      </Box>

      <DialogContent sx={{ padding: 3, paddingTop: 0 }}>
        {reportSent ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            gap={2}
          >
            <SvgToIcon
              IconLogo={inputIcons.checkCircleIcon}
              style={{
                width: "48px",
                height: "48px",
                color: colors.success_main,
              }}
            />
            <Typography variant="h6" fontWeight={600} align="center">
              Message received!
            </Typography>
            <Typography
              variant="body2"
              align="center"
              sx={{ color: colors.text_secondary }}
            >
              We'll take a look and figure out what went wrong. You're all set
              to continue working.
            </Typography>
          </Box>
        ) : (
          <>
            <StyledTextField
              margin="dense"
              label="What happened?"
              placeholder="Tell us what you were doing right before the error"
              multiline
              minRows={4}
              maxRows={4}
              fullWidth
              value={userMessage}
              onChange={(e) => {
                if (e.target.value.length <= MAX_CHARACTERS) {
                  setUserMessage(e.target.value);
                }
              }}
              variant="outlined"
              InputLabelProps={textFieldLabelProps()}
              InputProps={getTextFieldInputProps({
                sx: {
                  padding: 1,
                  "& textarea": {
                    padding: "14px",
                  },
                },
              })}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    colors.textfield_outlined_enabled_auto_generated_bg,
                },
              }}
            />
            <Box display="flex" alignItems="center" gap={0.5} mt={1} ml="12px">
              <SvgToIcon
                IconLogo={inputIcons.infoIcon}
                style={{
                  width: "12px",
                  height: "12px",
                  color: colors.text_help,
                }}
              />
              <Typography variant="caption" sx={{ color: colors.text_help }}>
                Optional — up to {MAX_CHARACTERS} characters. Error details are
                included automatically.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
        {reportSent ? (
          <>
            <OutlinedButton
              onClick={navigateHome}
              color="inherit"
              size="medium"
            >
              Go to {homePath === "/posture/controls" ? "Controls" : "Overview"}
            </OutlinedButton>
            <Box sx={{ flex: 1 }} />
            <ContainedButton onClick={handleClose} color="primary" size="small">
              Close
            </ContainedButton>
          </>
        ) : (
          <>
            <OutlinedButton
              onClick={navigateHome}
              color="inherit"
              size="medium"
              disabled={isSubmitting}
            >
              Back to home
            </OutlinedButton>
            <Box sx={{ flex: 1 }} />
            <OutlinedButton
              onClick={handleClose}
              color="inherit"
              size="small"
              disabled={isSubmitting}
            >
              Cancel
            </OutlinedButton>
            <ContainedButton
              onClick={handleSendReport}
              color="primary"
              size="small"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send report"}
            </ContainedButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactSupportModal;
